import {
  SET_CONVERSATIONS,
  UPDATE_CONVERSATION_LATEST,
  SET_CONVERSATIONS_LOADING,
  SET_CONVERSATIONS_ERROR,
  SET_ACTIVE_CONVERSATION_USER,
  SET_MESSAGES,
  ADD_MESSAGE,
  SET_MESSAGES_LOADING,
  SET_MESSAGES_ERROR,
  SET_SENDING_MESSAGE,
  SET_SENDING_MESSAGE_ERROR,
} from "../Types/AdminChatTypes";

const initialState = {
  conversations: [],
  totalUnreadCount: 0,
  loadingConversations: true,
  errorConversations: null,
  activeConversationUserId: null,
  activeConversationMessages: [],
  loadingActiveConversation: false,
  errorActiveConversation: null,
  sendingAdminMessage: false,
  errorSendingMessage: null,
};

const AdminChatReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_CONVERSATIONS_LOADING:
      return { ...state, loadingConversations: action.payload };
    case SET_CONVERSATIONS:
      const initialUnreadCount = action.payload.reduce(
        (acc, convo) => acc + (convo.unread_count || 0),
        0
      );
      return {
        ...state,
        loadingConversations: false,
        conversations: action.payload,
        totalUnreadCount: initialUnreadCount,
      };
    case SET_CONVERSATIONS_ERROR:
      return {
        ...state,
        loadingConversations: false,
        errorConversations: action.payload,
      };

    case UPDATE_CONVERSATION_LATEST:
      const { message, activeUserId } = action.payload;
      const conversationIndex = state.conversations.findIndex(
        (c) => c.user_id === message.user_id
      );

      let newConversations = [...state.conversations];

      if (conversationIndex > -1) {
        const conversationToUpdate = newConversations[conversationIndex];

        const isChatActive = activeUserId === message.user_id;

        const updatedConvo = {
          ...conversationToUpdate,
          id: message.id,
          message: message.message,
          is_admin: message.is_admin,
          created_at: message.created_at,

          // --- Conditionally update read status based on context ---
          // If the chat is active, it's "read". Otherwise, keep the old read status.
          read_at: isChatActive
            ? new Date().toISOString()
            : conversationToUpdate.read_at,
          // If the chat is active, the unread count is 0. Otherwise, increment it.
          unread_count: isChatActive
            ? 0
            : (conversationToUpdate.unread_count || 0) + 1,
        };

        // Move the updated conversation to the top of the list
        newConversations.splice(conversationIndex, 1);
        newConversations.unshift(updatedConvo);
      } else {
        // --- Scenario 2: A brand new user's first ever message ---
        const newConvo = {
          ...message,
          unread_count: 1, // First message is always 1 unread
        };
        newConversations.unshift(newConvo);
      }

      const totalUnreadCount = newConversations.reduce(
        (acc, convo) => acc + (convo.unread_count || 0),
        0
      );
      return {
        ...state,
        conversations: newConversations,
        totalUnreadCount,
      };

    // --- Active Chat Window Reducers ---
    case SET_ACTIVE_CONVERSATION_USER:
      if (state.activeConversationUserId !== action.payload) {
        // When we click a conversation, we optimistically set its unread count to 0 in the UI
        const updatedConversations = state.conversations.map((convo) => {
          if (convo.user_id === action.payload) {
            return {
              ...convo,
              unread_count: 0,
              read_at: new Date().toISOString(),
            };
          }
          return convo;
        });

        const newTotalUnreadCount = updatedConversations.reduce(
          (acc, convo) => acc + (convo.unread_count || 0),
          0
        );
        return {
          ...state,
          activeConversationUserId: action.payload,
          activeConversationMessages: [],
          conversations: updatedConversations,
          totalUnreadCount: newTotalUnreadCount,
        };
      }
      return state;

    case SET_MESSAGES_LOADING:
      return { ...state, loadingActiveConversation: action.payload };
    case SET_MESSAGES:
      return {
        ...state,
        loadingActiveConversation: false,
        activeConversationMessages: action.payload,
      };
    case SET_MESSAGES_ERROR:
      return {
        ...state,
        loadingActiveConversation: false,
        errorActiveConversation: action.payload,
      };

    case ADD_MESSAGE:
      // This only adds the message to the right-hand panel if it's the active chat
      if (state.activeConversationUserId === action.payload.user_id) {
        return {
          ...state,
          activeConversationMessages: [
            ...state.activeConversationMessages,
            action.payload,
          ],
        };
      }
      return state;

    // --- Message Sending UI Reducers ---
    case SET_SENDING_MESSAGE:
      return { ...state, sendingAdminMessage: action.payload };
    case SET_SENDING_MESSAGE_ERROR:
      return { ...state, errorSendingAdminMessage: action.payload };

    default:
      return state;
  }
};

export default AdminChatReducer;
